import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";
import { RemittanceDocSchema } from "./normalize-remittance-document.step";
import { deductionSchema, invoiceReferenceSchema } from "./remittance-schemas";

/**
 * Create Pending Payment Step
 *
 * Creates an AR payment record (status: PENDING) from a normalized remittance
 * document. The payment is stored for later matching against Plaid bank
 * transactions.
 *
 * Note: This is a backend-only step that requires database access.
 * The handler is registered in backend/src/workflows/steps/handlers/
 */

export const PAYMENT_METHODS = [
  "check",
  "wire",
  "ach",
  "eft",
  "credit_card",
  "other",
] as const;

export const createPendingPaymentStep = defineStepConfig({
  type: "create_pending_payment",
  name: "Create Pending Payment",
  description:
    "Create a pending AR payment record from a normalized remittance document",
  category: "Data Access",
  libraryModule: "AccountsReceivable",
  inputSchema: z.object({
    status: z
      .enum(["success", "extraction_failed"])
      .describe("Normalization status from the upstream step"),
    failureReason: z.string().optional(),
    extractionResultId: z.uuid().optional(),
    remittanceDoc: RemittanceDocSchema.optional().describe(
      "Normalized remittance fields from the upstream normalize_remittance_document step. Present when status is success.",
    ),
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email to link the payment to",
    ),
    document: entityOfType(SystemEntity.CORE_DOCUMENT)
      .optional()
      .describe(
        "Source document — populates remittance_advice.document_id and disambiguates dedup keys for emails with multiple attachments",
      ),
    customer: entityOfType(SystemEntity.AR_CUSTOMER)
      .nullable()
      .optional()
      .describe(
        "The customer the payment belongs to, identified upstream by identify_customer. When null or absent, the advice persists with payment_id IS NULL and the step returns customer_unmatched.",
      ),
  }),

  outputSchema: z.object({
    status: z
      .enum(["success", "extraction_failed", "customer_unmatched"])
      .describe(
        "'success' when payment created; 'extraction_failed' passthrough; 'customer_unmatched' when customer could not be identified",
      ),
    failureReason: z.string().optional(),
    payment: entityOfType(SystemEntity.AR_PAYMENT).optional(),
    paymentId: z.uuid().optional(),
    remittanceAdviceId: z
      .uuid()
      .optional()
      .describe(
        "ar_remittance_advice id for the recorded intake. Present whenever the advice was persisted (success and customer_unmatched).",
      ),
    invoiceReferences: z.array(invoiceReferenceSchema),
    deductions: z.array(deductionSchema).optional(),
  }),

  configSchema: z.object({}),

  emits: [] as const,
});
